import React, { useEffect, useState } from 'react';
import { fs } from '../../firebase';
import { collection, query, where, getDocs, updateDoc, doc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import API_ENDPOINTS from '../../config/api';
import { formatOrderTypeForAdmin } from '../../utils/orderFulfillment';
import '../../css/LoyaltyCrm.css'
const DeliveryCrmInterface = () => {
    const [orders, setOrders] = useState([]);
    const [filteredOrders, setFilteredOrders] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [isSyncing, setIsSyncing] = useState(false);
    const [lastSyncTime, setLastSyncTime] = useState(null);
    const auth = getAuth();

    useEffect(() => {
        fetchOrders().then(() => {
            // Auto-sync on load
            syncOrderStatuses();
        });
    }, []);

    const fetchOrders = async () => {
        try {
            setLoading(true);
            const ordersRef = collection(fs, "DeliveryOrder");
            const q = query(ordersRef, where("status", "in", ["pending", "processing", "completed"]));
            const querySnapshot = await getDocs(q);
            const fetchedOrders = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                selectedStatus: doc.data().status
            }));
            setOrders(fetchedOrders);
            setFilteredOrders(fetchedOrders);
        } catch (error) {
            console.error("Ошибка при загрузке заказов:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleStatusChange = async (orderId, newStatus) => {
        try {
            // If status is being changed to declined, cancel in Syrve first
            if (newStatus === 'declined') {
                // Find the order to get Syrve IDs
                const order = orders.find(o => o.id === orderId);
                
                if (order && order.syrveOrderIds) {
                    console.log('Cancelling order in Syrve:', order.syrveOrderIds);
                    
                    // Get access token first
                    const tokenRes = await fetch(API_ENDPOINTS.proxy.accessToken, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' }
                    });
                    const tokenData = await tokenRes.json();
                    const token = tokenData.token;
                    
                    // Cancel each Syrve order
                    for (const [venue, syrveOrderId] of Object.entries(order.syrveOrderIds)) {
                        const organizationId = order.syrveOrganizationIds?.[venue];
                        
                        if (organizationId && syrveOrderId) {
                            try {
                                const cancelRes = await fetch(API_ENDPOINTS.proxy.deliveriesCancel, {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({
                                        token,
                                        organizationId,
                                        orderId: syrveOrderId
                                    })
                                });
                                
                                const cancelResult = await cancelRes.json();
                                
                                if (!cancelRes.ok) {
                                    console.error(`Failed to cancel order in ${venue}:`, cancelResult);
                                } else {
                                    console.log(`Order cancelled in ${venue}:`, cancelResult);
                                }
                            } catch (err) {
                                console.error(`Error cancelling order in ${venue}:`, err);
                            }
                        }
                    }
                }
            }
            
            // Update Firebase status
            const orderRef = doc(fs, "DeliveryOrder", orderId);
            await updateDoc(orderRef, { 
                status: newStatus,
                ...(newStatus === 'declined' ? { cancelledAt: new Date() } : {})
            });
            
            setOrders(prevOrders =>
                prevOrders.map(order =>
                    order.id === orderId ? { ...order, status: newStatus, selectedStatus: newStatus } : order
                )
            );
            setFilteredOrders(prevOrders =>
                prevOrders.map(order =>
                    order.id === orderId ? { ...order, status: newStatus, selectedStatus: newStatus } : order
                )
            );
            alert('Статус заказа обновлён');
        } catch (error) {
            console.error("Ошибка при изменении статуса:", error);
            alert('Ошибка при изменении статуса');
        }
    };

    const handleSearch = (e) => {
        const term = e.target.value.toLowerCase();
        setSearchTerm(term);
        const filtered = orders.filter(order => {
            return (
                order.recipientName?.toLowerCase().includes(term) ||
                order.phoneNumber?.toLowerCase().includes(term) ||
                order.comment?.toLowerCase().includes(term) ||
                order.destination?.address?.toLowerCase().includes(term)
            );
        });
        setFilteredOrders(filtered);
    };

    const syncOrderStatuses = async () => {
        setIsSyncing(true);
        try {
            const response = await fetch(API_ENDPOINTS.syncOrderStatuses, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            
            const result = await response.json();
            if (result.success) {
                setLastSyncTime(new Date());
                // Refresh orders after sync
                await fetchOrders();
            }
        } catch (error) {
            console.error("Error syncing order statuses:", error);
        } finally {
            setIsSyncing(false);
        }
    };

    if (loading) {
        return <div className='ordercrm-loading'>Загрузка заказов...</div>;
    }

    return (
        <div className='ordercrm-container'>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h1 className='ordercrm-title'>Админский кабинет: Управление заказами</h1>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    {lastSyncTime && (
                        <span style={{ fontSize: '14px', color: '#666' }}>
                            Синхронизировано: {lastSyncTime.toLocaleTimeString()}
                        </span>
                    )}
                    <button
                        onClick={syncOrderStatuses}
                        disabled={isSyncing}
                        style={{
                            padding: '8px 16px',
                            backgroundColor: '#28a745',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: isSyncing ? 'not-allowed' : 'pointer',
                            opacity: isSyncing ? 0.6 : 1
                        }}
                    >
                        {isSyncing ? 'Синхронизация...' : 'Синхронизировать с Syrve'}
                    </button>
                </div>
            </div>

            <input
                type="text"
                value={searchTerm}
                onChange={handleSearch}
                placeholder="Поиск по имени, телефону, комментарию, адресу..."
                className="ordercrm-search"
            />

            <table className='ordercrm-table'>
                <thead>
                <tr>
                    <th>Номер заказа</th>
                    <th>Способ получения заказа</th>
                    <th>Имя получателя</th>
                    <th>Телефон</th>
                    <th>Метод оплаты</th>
                    <th>Цена</th>
                    <th>Кол-во персон</th>
                    <th>Время доставки</th>
                    <th>Комментарий</th>
                    <th>Адрес доставки</th>
                    <th>Подъезд</th>
                    <th>Этаж</th>
                    <th>Продукты</th>
                    <th>Статус</th>
                    <th>Syrve статус</th>
                    <th>Действия</th>
                </tr>
                </thead>
                <tbody>
                {filteredOrders.map((order) => (
                    <tr key={order.id} className='ordercrm-row'>
                        <td>{order.id}</td>
                        <td>{formatOrderTypeForAdmin(order.orderType)}</td>
                        <td>{order.recipientName}</td>
                        <td>{order.phoneNumber}</td>
                        <td>{order.paymentMethod}</td>
                        <td>{order.totalPrice}</td>
                        <td>{order.personsCount}</td>
                        <td>
                            {order.deliveryTime?.seconds
                                ? new Date(order.deliveryTime.seconds * 1000).toLocaleString('ru-RU')
                                : '—'}
                        </td>

                        <td>{order.comment}</td>
                        <td className='ordercrm-address'>{order.destination?.address || 'Адрес не указан'}</td>
                        <td>{order.entrance}</td>
                        <td>{order.floor}</td>
                        <td>
                            {(order.products || []).map((product, index) => (
                                <div key={index} className='ordercrm-product'>
                                    {product.productId} - {product.name} - {product.quantity} шт. - {product.price}₾
                                </div>
                            ))}
                        </td>
                        <td>
                            {order.isPrinted ? (
                                <span style={{ 
                                    padding: '5px 10px', 
                                    backgroundColor: '#ff9800', 
                                    color: 'white', 
                                    borderRadius: '4px',
                                    fontWeight: 'bold'
                                }}>
                                    🍳 Готовится
                                </span>
                            ) : (
                                <select
                                    value={order.selectedStatus}
                                    onChange={(e) => {
                                        const newStatus = e.target.value;
                                        setFilteredOrders(prev =>
                                            prev.map(o => o.id === order.id ? { ...o, selectedStatus: newStatus } : o)
                                        );
                                    }}
                                    className='ordercrm-select'
                                >
                                    <option className='ordercrm-option' value="pending">Ожидает</option>
                                    <option className='ordercrm-option' value="confirmed">Подтвержден</option>
                                    <option className='ordercrm-option' value="processing">Обрабатывается</option>
                                    <option className='ordercrm-option' value="delivering">В пути</option>
                                    <option className='ordercrm-option' value="declined">Отклонён</option>
                                    <option className='ordercrm-option' value="completed">Завершён</option>
                                </select>
                            )}
                        </td>
                        <td style={{ fontSize: '12px' }}>
                            {order.syrveStatuses && Object.entries(order.syrveStatuses).map(([venue, status]) => (
                                <div key={venue}>
                                    <strong>{venue}:</strong> {status || 'N/A'}
                                </div>
                            ))}
                        </td>
                        <td>
                            <button
                                onClick={() => handleStatusChange(order.id, order.selectedStatus)}
                                className='ordercrm-button'
                            >
                                Обновить статус
                            </button>
                        </td>
                    </tr>
                ))}
                </tbody>
            </table>
        </div>
    );
};

export default DeliveryCrmInterface;
